import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const FEATURE_EMAIL = 'info@axxsky.com';
const SUPPORT_EMAIL = 'support@yourcompany.com';

const APP_FEATURES = [
  'Order management',
  'Table management',
  'Kitchen order processing',
  'Billing and invoicing',
  'Payment processing - Does not collect payment itself',
  'Inventory management',
  'Staff management',
  'Sales reporting and analytics',
  'Customer management',
];

const SUPPORT_TOPICS = [
  'Login and account access',
  'Device setup and configuration',
  'Printer and hardware connectivity',
  'Order or billing issues',
  'Payment-Method questions',
  'Inventory management',
  'Data backup and restoration',
  'Software updates and new features',
];

const FAQS = [
  {
    id: 'login',
    question: "I can't log in.",
    answer:
      "Verify that you're using the correct credentials and that your internet connection is active. If you've forgotten your password, use the password reset option or contact support.",
  },
  {
    id: 'printer',
    question: 'My printer is not printing receipts.',
    answer:
      'Ensure the printer is powered on, connected to the same network as the POS device, and properly configured in the app settings. Closely look into IPs and Backup Printers. For multiple admins, please check if the Multiple Printer option is turned on and there are more than 1 admin printer.',
  },
  {
    id: 'update',
    question: 'How do I update the app?',
    answer:
      'Updates are available through the Apple App Store. Keeping the app updated ensures you have the latest features, improvements, and security updates. An official email will be shared with the list of features whenever an update is made.',
  },
  {
    id: 'security',
    question: 'Is my data secure?',
    answer:
      'Yes. We use industry-standard security practices to help protect your business data and customer information. At the server side, a robust and strong isolation is made to prevent data leak.',
  },
];

const SECTIONS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'what-the-app-does', label: 'What the App Does' },
  { id: 'need-help', label: 'Need Help?' },
  { id: 'common-topics', label: 'Common Support Topics' },
  { id: 'contact', label: 'Contact Support' },
  { id: 'faq', label: 'Frequently Asked Questions' },
  { id: 'feature-requests', label: 'Feature Requests' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'thank-you', label: 'Thank You' },
];

const Support = () => {
  const [openFaqId, setOpenFaqId] = useState(FAQS[0].id);

  const toggleFaq = (id) => {
    setOpenFaqId((current) => (current === id ? null : id));
  };

  return (
    <div className="privacy-page">
      <main className="privacy-main">
        <div className="privacy-layout">
          <nav className="privacy-toc" aria-label="Table of contents">
            <p className="privacy-toc-title">Contents</p>
            <ol className="privacy-toc-list">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.label}</a>
                </li>
              ))}
            </ol>
          </nav>

          <article className="privacy-content">
            <header className="privacy-title-block">
              <p className="privacy-eyebrow">Help</p>
              <h1>Support</h1>
              <p className="privacy-meta">Restaurant POS Support</p>
            </header>

            <section id="welcome" className="privacy-section">
            <h2>Welcome to Restaurant POS Support</h2>
            <p>
              Thank you for using our Restaurant POS app. We&apos;re here to help you get the most out of
              your restaurant management system.
            </p>
          </section>

          <section id="what-the-app-does" className="privacy-section">
            <h2>What the App Does</h2>
            <p>
              Our Restaurant POS app is designed to simplify and streamline your restaurant&apos;s daily
              operations, including:
            </p>
            <ul>
              {APP_FEATURES.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </section>

          <section id="need-help" className="privacy-section">
            <h2>Need Help?</h2>
            <p>
              If you&apos;re experiencing issues or have questions about using the app, our support team is
              ready to assist.
            </p>
          </section>

          <section id="common-topics" className="privacy-section">
            <h2>Common Support Topics</h2>
            <ul>
              {SUPPORT_TOPICS.map((topic) => (
                <li key={topic}>{topic}</li>
              ))}
            </ul>
          </section>

          <section id="contact" className="privacy-section">
            <h2>Contact Support</h2>
            <address className="privacy-address">
              <p className="privacy-address-header">
                <i className="pi pi-headphones" aria-hidden="true" />
                Get in touch
              </p>
              <div className="privacy-address-body">
                <div className="privacy-address-rows">
                  <div className="privacy-address-row">
                    <i className="pi pi-envelope" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Email</span>
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="privacy-address-row-value">
                        {SUPPORT_EMAIL}
                      </a>
                    </div>
                  </div>
                  <div className="privacy-address-row">
                    <i className="pi pi-clock" aria-hidden="true" />
                    <div>
                      <span className="privacy-address-row-label">Business Hours</span>
                      <span className="privacy-address-row-value">Monday – Friday</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="privacy-address-note">
                We aim to respond to all support requests within one business day.
              </p>
            </address>
          </section>

          <section id="faq" className="privacy-section">
            <h2>Frequently Asked Questions</h2>
            <div className="support-faq-list">
              {FAQS.map((faq) => {
                const isOpen = openFaqId === faq.id;
                return (
                  <div
                    key={faq.id}
                    className={`support-faq-item${isOpen ? ' is-open' : ''}`}
                  >
                    <button
                      type="button"
                      className="support-faq-question"
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${faq.id}`}
                      id={`faq-question-${faq.id}`}
                      onClick={() => toggleFaq(faq.id)}
                    >
                      <span>{faq.question}</span>
                      <i className={`pi ${isOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
                    </button>
                    {isOpen && (
                      <div
                        id={`faq-answer-${faq.id}`}
                        className="support-faq-answer"
                        role="region"
                        aria-labelledby={`faq-question-${faq.id}`}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section id="feature-requests" className="privacy-section">
            <h2>Feature Requests</h2>
            <p>
              We&apos;re continuously improving our application. If you have ideas or suggestions, we&apos;d
              love to hear them. Send your feedback to:{' '}
              <a href={`mailto:${FEATURE_EMAIL}`}>{FEATURE_EMAIL}</a>
            </p>
          </section>

          <section id="privacy" className="privacy-section">
            <h2>Privacy</h2>
            <p>
              Your privacy is important to us. Please refer to our{' '}
              <Link to="/privacy">Privacy Policy</Link> for details about how your information is collected,
              used, and protected.
            </p>
          </section>

          <section id="thank-you" className="privacy-section privacy-section-last">
            <h2>Thank You</h2>
            <p>
              Thank you for choosing our Restaurant POS app. We appreciate your business and are committed to
              helping your restaurant operate efficiently.
            </p>
          </section>
          </article>
        </div>
      </main>
    </div>
  );
};

export default Support;
